/**
 * GMA-1616 (CompassCare Web 1/14 — Foundation) — shared right-side detail drawer.
 *
 * ONE drawer reused for every detail type (client / alert / device / activity);
 * callers slot the type-specific body as `children`. Surface/border come from
 * the single design-token set — no per-page colors.
 *
 * Accessibility (GMA-1616 AC — opens/closes with keyboard + screen-reader
 * support): the panel is a labelled modal dialog (role="dialog",
 * aria-modal, aria-label = title); MUI's Drawer traps focus and closes on
 * backdrop click and on Escape (handled by MUI's Modal, which calls onClose
 * with reason "escapeKeyDown"). The close control is an icon button with a
 * 44px hit target and a "Close" aria-label.
 */
import React from "react";
import PropTypes from "prop-types";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { theme } from "../../styles/designTokens";

export default function DetailDrawer({
  open,
  onClose,
  title,
  type,
  children,
  width = 420,
}) {
  return (
    <Drawer
      anchor="right"
      open={open}
      // Rely on MUI Modal's built-in Escape handling: it listens at the Modal
      // root (not just the focused Paper child), so Escape closes the drawer
      // regardless of where focus sits, calling onClose(event, "escapeKeyDown").
      // A custom onKeyDown on PaperProps would miss Escape when focus is on the
      // Modal wrapper rather than a Paper descendant. Backdrop click still fires
      // onClose(event, "backdropClick").
      onClose={onClose}
      PaperProps={{
        role: "dialog",
        "aria-modal": "true",
        "aria-label": title,
        "data-testid": "detail-drawer",
        "data-detail-type": type || "generic",
        // Token colors applied inline (not via a stylesheet) so the drawer's
        // chrome comes straight from the single design-token set and takes
        // precedence over ordinary ambient CSS. (Inline styles can still be
        // overridden by a later `!important` rule — this is a default, not a
        // hard guarantee.)
        style: {
          width,
          maxWidth: "100vw",
          backgroundColor: theme.surface,
          borderLeft: `1px solid ${theme.border}`,
          color: theme.textPrimary,
          fontFamily: theme.fontUI,
        },
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16, fontFamily: theme.fontUI }}>
          {title}
        </h2>
        <IconButton
          aria-label="Close"
          onClick={(event) => onClose(event, "closeButton")}
          size="small"
          sx={{ color: theme.textPrimary, minWidth: 44, minHeight: 44 }}
        >
          <CloseIcon />
        </IconButton>
      </header>
      <div style={{ padding: 16 }}>{children}</div>
    </Drawer>
  );
}

DetailDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  /** Called as onClose(event, reason) where reason is escapeKeyDown | closeButton | backdropClick. */
  onClose: PropTypes.func.isRequired,
  /** Accessible name for the dialog + header text. */
  title: PropTypes.string.isRequired,
  /** Detail type (client|alert|device|activity|…) exposed as data-detail-type. */
  type: PropTypes.string,
  children: PropTypes.node,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
